import * as XLSX from 'xlsx';

export interface CompanyData {
  CompanyName: string;
  CompanyGeography: string;
}

export interface ExcelProcessingResult {
  companies: CompanyData[];
  totalProcessed: number;
  errors: string[];
}

export class ExcelProcessingService {
  /**
   * Process Excel file and extract company and geography data
   */
  static async processExcelFile(file: File): Promise<ExcelProcessingResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const result = this.parseExcelData(e.target?.result as ArrayBuffer);
          resolve(result);
        } catch (error) {
          reject(error); // Pass the original error without wrapping
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read Excel file'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse Excel data from ArrayBuffer
   */
  private static parseExcelData(data: ArrayBuffer): ExcelProcessingResult {
    const workbook = XLSX.read(data, { type: 'array' });

    // Get the first worksheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert to JSON
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

    if (jsonData.length === 0) {
      throw new Error('Excel file appears to be empty');
    }

    // Get headers and find column indices
    const headers = jsonData[0].map(header => header?.toString().trim().toLowerCase());
    const companyIndex = this.findColumnIndex(headers, ['company', 'company name', 'companyname']);
    const geographyIndex = this.findColumnIndex(headers, ['geography', 'location', 'country', 'region']);

    if (companyIndex === -1) {
      throw new Error('Could not find "Company" column in Excel file');
    }

    if (geographyIndex === -1) {
      throw new Error('Could not find "Geography" column in Excel file');
    }

    // Check row limit (max 50 companies per file)
    const maxRows = 50;
    const dataRows = jsonData.length - 1; // Exclude header row

    if (dataRows > maxRows) {
      throw new Error(`Excel file contains too many companies. Maximum allowed is ${maxRows} companies per file. Your file contains ${dataRows} companies.`);
    }

    const companies: CompanyData[] = [];
    const errors: string[] = [];

    // Process data rows (skip header row)
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];

      if (!row || row.length === 0) {
        continue; // Skip empty rows
      }

      const companyName = row[companyIndex]?.toString().trim();
      const geography = row[geographyIndex]?.toString().trim();

      if (!companyName) {
        errors.push(`Row ${i + 1}: Missing company name`);
        continue;
      }

      if (!geography) {
        errors.push(`Row ${i + 1}: Missing geography for company "${companyName}"`);
        continue;
      }

      // Validate company name length
      if (companyName.length > 100) {
        errors.push(`Row ${i + 1}: Company name too long (max 100 characters): "${companyName}"`);
        continue;
      }

      // Validate geography length
      if (geography.length > 50) {
        errors.push(`Row ${i + 1}: Geography too long (max 50 characters): "${geography}"`);
        continue;
      }

      companies.push({
        CompanyName: companyName,
        CompanyGeography: geography
      });
    }

    return {
      companies,
      totalProcessed: companies.length,
      errors
    };
  }

  /**
   * Find column index by matching possible column names
   */
  private static findColumnIndex(headers: string[], possibleNames: string[]): number {
    for (const name of possibleNames) {
      const index = headers.findIndex(header => header === name);
      if (index !== -1) {
        return index;
      }
    }
    return -1;
  }

  /**
   * Validate extracted company data
   */
  static validateCompanyData(companies: CompanyData[]): { valid: CompanyData[]; invalid: { data: CompanyData; reason: string }[] } {
    const valid: CompanyData[] = [];
    const invalid: { data: CompanyData; reason: string }[] = [];

    for (const company of companies) {
      const validationError = this.validateSingleCompany(company);

      if (validationError) {
        invalid.push({ data: company, reason: validationError });
      } else {
        valid.push(company);
      }
    }

    return { valid, invalid };
  }

  /**
   * Validate a single company record
   */
  private static validateSingleCompany(company: CompanyData): string | null {
    // Company name validation
    if (!company.CompanyName || company.CompanyName.trim().length === 0) {
      return 'Company name is required';
    }

    if (company.CompanyName.length > 100) {
      return 'Company name too long (max 100 characters)';
    }

    // Basic pattern validation for company name
    const companyNamePattern = /^[a-zA-Z0-9\s\-&.,()]+$/;
    if (!companyNamePattern.test(company.CompanyName)) {
      return 'Company name contains invalid characters';
    }

    // Geography validation
    if (!company.CompanyGeography || company.CompanyGeography.trim().length === 0) {
      return 'Geography is required';
    }

    if (company.CompanyGeography.length > 50) {
      return 'Geography too long (max 50 characters)';
    }

    // Basic pattern validation for geography
    const geographyPattern = /^[a-zA-Z\s\-,]+$/;
    if (!geographyPattern.test(company.CompanyGeography)) {
      return 'Geography contains invalid characters';
    }

    return null;
  }
}